import { ReactElement, useEffect, useState } from "react";
import { IContentAreaProps, IFilterData, IState } from "../../../../types";
import FilterComponentBase from "../../../../components/filterComponentBase";
import { DateTime } from "luxon";
import { Alert } from "@mui/material";
import { IBarChartConfig, IPoint, ISeriesWithAxes, IStackedBarChartConfig } from "../../../../components/charts/types";
import { roundUp } from "../../../../system/utils";
import BarChart from "../../../../components/charts/barChart";
import {
  IDelayInfo,
  IDelayInfoByArea,
  IDelayInfoByAreaGroup,
  IDelayInfoByDay,
  IDelayReport
} from "../crewDetail/types";
import { useSelector } from "react-redux";
import { getPalette } from "../../../../configuration/configuration";
import { useFetch } from "../../../../system/useFetch";
import StackedBarChart from "../../../../components/charts/stackedBarChart";
import SkeletonView from "../../../../components/skeletonView";

interface ICharts {
  delaysByCrew: IStackedBarChartConfig;
  delayInfoByDay: IStackedBarChartConfig;
  delayInfoByAreaGroup: IStackedBarChartConfig;
  delayInfoByArea: IBarChartConfig;
}

const DelaysReport = ({ parentSize }: IContentAreaProps): ReactElement => {
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const { loading, error, data } = useFetch<IDelayReport>({ module: "meltShop", query: "getDelayReport", payload: { ...filter.remote } });
  const [chartsConfig, setChartsConfig] = useState<ICharts | null>(null);

  useEffect(() => {
    if (data) {

      const seriesA: ISeriesWithAxes[] = [];
      const seriesB: ISeriesWithAxes[] = [];
      const seriesC: ISeriesWithAxes[] = [];
      const seriesD: ISeriesWithAxes[] = [];

      const categoriesA: string[] = [];
      const categoriesB: string[] = [];
      const categoriesC: string[] = [];
      const categoriesD: string[] = [];

      let maximumA = 0;
      let maximumB = 0;
      let maximumC = 0;
      let maximumD = 0;

      const hoursA: any = {};
      const hoursB: any = {};
      const hoursC: any = {};

      data.delayInfoByDay.forEach((row: IDelayInfoByDay) => {
        let item = seriesA.find(x => x.name === row.category);
        if (!item) {
          item = {
            name: row.category,
            xAxisName: "Day",
            yAxisName: "Hours",
            value: [],
            color: row.categoryColor,
            unit: "h"
          };

          seriesA.push(item);
        }

        item.color = item.color ?? row.categoryColor;
        (item.value as IPoint[]).push({ x: `${row.factoryDay}`, y: row.hours });

        if (!hoursA[`${row.factoryDay}`]) {
          hoursA[`${row.factoryDay}`] = row.hours;
        } else {
          hoursA[`${row.factoryDay}`] += row.hours;
        }

        if (!categoriesA.find(x => x === `${row.factoryDay}`)) {
          categoriesA.push(`${row.factoryDay}`);
        }
      });

      Object.keys(hoursA).forEach(key => {
        maximumA = Math.max(maximumA, hoursA[key]);
      });

      data.delaysByCrew.forEach((row: IDelayInfo) => {
        let item = seriesB.find(x => x.name === row.category);
        if (!item) {
          item = {
            name: row.category,
            xAxisName: "CREW",
            yAxisName: "Hours",
            value: [],
            color: row.categoryColor,
            unit: "h"
          };

          seriesB.push(item);
        }

        item.color = item.color ?? row.categoryColor;

        (item.value as IPoint[]).push({ x: row.crewName, y: row.hours });

        if (!hoursB[row.crewName]) {
          hoursB[row.crewName] = row.hours;
        } else {
          hoursB[row.crewName] += row.hours;
        }

        if (!categoriesB.find(x => x === row.crewName)) {
          categoriesB.push(row.crewName);
        }
      });

      Object.keys(hoursB).forEach(key => {
        maximumB = Math.max(maximumB, hoursB[key]);
      });

      let colorIndex = 0;
      const palette: any = {};

      data.delayInfoByAreaGroup.forEach((row: IDelayInfoByAreaGroup) => {
        let item = seriesC.find(x => x.name === row.category);
        if (!item) {
          item = {
            name: row.category,
            xAxisName: "Area Group",
            yAxisName: "Hours",
            value: [],
            color: row.categoryColor,
            unit: "h"
          };

          seriesC.push(item);
        }

        item.color = item.color ?? row.categoryColor;
        if (!item.color) {
          if (!palette[row.category]) {
            palette[row.category] = getPalette().list[colorIndex];
            colorIndex++;
          }

          item.color = palette[row.category];
        }

        (item.value as IPoint[]).push({ x: row.areaGroup, y: row.hours });

        if (!hoursC[`${row.areaGroup}`]) {
          hoursC[`${row.areaGroup}`] = row.hours;
        } else {
          hoursC[`${row.areaGroup}`] += row.hours;
        }

        // if (!categoriesC.find(x => x === `${row.areaGroup}`)) {
        //   categoriesC.push(`${row.areaGroup}`);
        // }
      });

      const keysC = Object.keys(hoursC);
      const tempArray: IPoint[] = [];
      keysC.forEach(key => {
        tempArray.push({ x: key, y: hoursC[key] });
        maximumC = Math.max(maximumC, hoursC[key]);
      });


      for (let i = 0; i < keysC.length - 1; i++) {
        for (let j = i + 1; j < keysC.length; j++) {
          if (tempArray[i].y < tempArray[j].y) {
            const temp = tempArray[i];
            tempArray[i] = tempArray[j];
            tempArray[j] = temp;
          }
        }
      }

      tempArray.forEach(item => {
        categoriesC.push(item.x as string);
      });

      data.delayInfoByArea.forEach((row: IDelayInfoByArea) => {
        seriesD.push({
          name: row.area,
          yAxisName: "Hours",
          xAxisName: "Area Group - Area",
          value: row.hours,
          color: row.color,
          unit: "h"
        });

        categoriesD.push(row.area);
        maximumD = Math.max(maximumD, row.hours);
      });


      setChartsConfig({
        delaysByCrew: {
          xAxis: {
            name: "CREW",
            categories: categoriesB
          },
          yAxis: {
            name: "Hours"
          },
          title: "Hours by CREW and Responsibility",
          series: seriesB,
          showLegend: true
        },

        delayInfoByDay: {
          xAxis: {
            name: "Day",
            categories: categoriesA,
            formatTick: (value) => {
              const dt = parseInt(value as string, 10);

              return `${DateTime.fromSeconds(dt).day}`;
            }
          },
          yAxis: {
            name: "Hours"
          },
          title: "Hours by Day and Responsibility",
          series: seriesA,
          showLegend: true
        },

        delayInfoByAreaGroup: {
          xAxis: {
            name: "Area Group",
            categories: categoriesC
          },
          yAxis: {
            name: "Hours"
          },
          title: "Hours and Duration By Area Group and Responsibility",
          series: seriesC,
          showLegend: true
        },

        delayInfoByArea: {
          xAxis: {
            name: "Area Group - Area",
            categories: categoriesD,
            ticksAngle: 15
          },
          yAxis: [{
            name: "Hours",
            minimum: 0,
            maximum: roundUp(maximumD, 1),
            ticksCount: 3,
            formatTick: (value) => {
              return typeof value === "number" ? (value as number).toFixed(2) : (value as IPoint).y.toFixed(2);
            }
          }],
          title: "Hours and Duration By Area Group and Area",
          series: seriesD,
        },

      });
    }
  }, [data]);

  const layoutStyle = {
    ...parentSize,
    gridTemplateRows: "auto 1fr",
    gridTemplateColumns: "1fr",
  };

  const subLayoutStyle = {
    gridTemplateRows: "1fr 1fr",
    gridTemplateColumns: "40% 1fr",
    padding: "1em"
  };

  const isMobile = parentSize.width < 400 || parentSize.height < 400;

  return (
    <div className="display-grid" style={layoutStyle}>
      <FilterComponentBase loading={loading} />

      {loading && <SkeletonView  parentSize={parentSize}/>}
      {!loading && error && <Alert severity="warning">{error.message}</Alert>}
      {!loading && !error && !chartsConfig && <Alert severity="warning">No Data to Display.</Alert>}

      {!loading && !error && chartsConfig && !isMobile &&
      <div className="display-grid" style={subLayoutStyle}>
        <div>
          {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delayInfoByAreaGroup}/>}
        </div>
        <div>
          {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delayInfoByDay}/>}
        </div>
        <div>
          {chartsConfig && <StackedBarChart barChartConfig={chartsConfig?.delaysByCrew}/>}
        </div>
        <div>
          {chartsConfig && <BarChart barChartConfig={chartsConfig?.delayInfoByArea}/>}
        </div>
      </div>
      }

      {!loading && !error && chartsConfig && isMobile &&
        <div className="p-1" style={{ width: parentSize.width - 16 }}>
          <div style={{ height: 250 }}>
            {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delayInfoByAreaGroup}/>}
          </div>
          <div style={{ height: 250 }}>
            {chartsConfig && <StackedBarChart barChartConfig={chartsConfig.delayInfoByDay}/>}
          </div>
          <div style={{ height: 250 }}>
            {chartsConfig && <StackedBarChart barChartConfig={chartsConfig?.delaysByCrew}/>}
          </div>
          <div style={{ height: 250 }}>
            {chartsConfig && <BarChart barChartConfig={chartsConfig?.delayInfoByArea}/>}
          </div>
        </div>
      }
    </div>
  );
};

export default DelaysReport;
